import { useEffect, type ReactElement } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { setUnauthorizedHandler } from "./api/client";
import { useMe } from "./api/hooks";
import { AuthProvider } from "./lib/auth";
import { Spinner } from "./components/ui";
import { Shell, MasterDataLayout } from "./components/Shell";
import { Login } from "./pages/Login";
import { SearchPage } from "./pages/masterdata/SearchPage";
import { DuplicatesPage } from "./pages/masterdata/DuplicatesPage";
import { FieldsPage } from "./pages/masterdata/FieldsPage";
import { ImportsPage } from "./pages/imports/ImportsPage";
import { ImportDetailPage } from "./pages/imports/ImportDetailPage";
import { Soon } from "./pages/Soon";
import { UsersPage } from "./pages/admin/UsersPage";
import type { Me } from "./api/types";

/** Garde de navigation (confort) : la sécurité réelle reste le contrôle des permissions par l'API. */
function RequirePerm({ perm, me, children }: { perm: string; me: Me; children: ReactElement }) {
  return me.permissions.includes(perm) ? children : <Navigate to={home(me)} replace />;
}

function home(me: Me): string {
  if (me.permissions.includes("data.read")) return "/master-data";
  if (me.permissions.includes("prospection.execute")) return "/prospection";
  return "/resultats";
}

export function App() {
  const me = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.setQueryData(["me"], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      nav("/login", { replace: true, state: { from: loc.pathname } });
    });
  }, [qc, nav, loc.pathname]);

  if (me.isLoading) return <div className="login"><Spinner /></div>;
  if (!me.data) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc.pathname }} />} />
      </Routes>
    );
  }
  return (
    <AuthProvider me={me.data}>
      <Routes>
        <Route path="/login" element={<Navigate to={home(me.data)} replace />} />
        <Route element={<Shell />}>
          <Route path="/" element={<Navigate to={home(me.data)} replace />} />
          <Route path="/master-data" element={<RequirePerm perm="data.read" me={me.data}><MasterDataLayout /></RequirePerm>}>
            <Route index element={<SearchPage />} />
            <Route path="imports" element={<RequirePerm perm="data.import" me={me.data}><ImportsPage /></RequirePerm>} />
            <Route path="imports/:id" element={<RequirePerm perm="data.import" me={me.data}><ImportDetailPage /></RequirePerm>} />
            <Route path="duplicates" element={<DuplicatesPage />} />
            <Route path="fields" element={<FieldsPage />} />
          </Route>
          <Route path="/manager" element={<RequirePerm perm="mission.write" me={me.data}><Soon space="manager" /></RequirePerm>} />
          <Route path="/prospection" element={<RequirePerm perm="prospection.execute" me={me.data}><Soon space="prospection" /></RequirePerm>} />
          <Route path="/resultats" element={<RequirePerm perm="results.read" me={me.data}><Soon space="resultats" /></RequirePerm>} />
          <Route path="/admin/users" element={<RequirePerm perm="users.read" me={me.data}><UsersPage /></RequirePerm>} />
          <Route path="*" element={<Navigate to={home(me.data)} replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}
